import { iso2FromMrzNationality } from "./iso3166";
import { buscarOpcion, type FilaDeCatalogo } from "./referencias";

/** Una fila del catálogo de países; `code` es el ISO 3166 alfa-2 cuando el catálogo lo trae. */
export interface FilaDePais extends FilaDeCatalogo {
  code?: string | null;
}

const codigoDeLaFila = (fila: FilaDePais): string => (fila.code ?? "").trim().toUpperCase();

/**
 * La fila del catálogo de países que corresponde a la nacionalidad del MRZ.
 *
 * El MRZ da la nacionalidad en ISO 3166 alfa-3 (ARG) y el catálogo, si trae
 * códigos, los tiene en alfa-2 (AR): se resuelve por código y NUNCA por nombre.
 * Por nombre es lo que fallaba: ordenados alfabéticamente, «argelina» va antes
 * que «argentina», y ARG elegía Argelia. Un país sin fila queda sin elegir.
 *
 * La pantalla pública recibe los códigos en `lookups.Country`, en las filas que
 * los tienen. Un catálogo sin códigos (ninguna fila trae `code`) cae en
 * `buscarOpcion`, que no elige si el nombre es ambiguo.
 */
export function filaDeNacionalidad<T extends FilaDePais>(
  filas: readonly T[] | undefined,
  nacionalidadMrz: unknown
): T | undefined {
  if (!filas || nacionalidadMrz === null || nacionalidadMrz === undefined) return undefined;
  if (!filas.some((f) => codigoDeLaFila(f) !== "")) return buscarOpcion(filas, nacionalidadMrz);
  const iso2 = iso2FromMrzNationality(String(nacionalidadMrz));
  return iso2 ? filas.find((f) => codigoDeLaFila(f) === iso2) : undefined;
}

/** Lo que se necesita de una fila cruda de la caché de referencias de países. */
export interface PaisDeLaCache {
  id: string;
  code?: string | null;
}

/**
 * La fila de las opciones de nacionalidad de un formulario que corresponde a la
 * nacionalidad del MRZ.
 *
 * Las opciones son gentilicios y no traen el código del país: el código está en
 * las filas crudas de la caché de referencias, que no traen el nombre. Con la
 * caché se busca el país por código y, con su id, la fila de las opciones —solo
 * por id exacto: el listado puede estar filtrado por lo que se escribió en el
 * campo, y buscar el nombre ahí podría caer en otro país—. Con la caché vacía
 * (todavía no cargó, o falló) queda el camino por nombre de `filaDeNacionalidad`.
 */
export function filaDeNacionalidadDelFormulario<T extends FilaDeCatalogo>(
  opciones: readonly T[] | undefined,
  paisesDeLaCache: readonly (PaisDeLaCache | null | undefined)[] | undefined,
  nacionalidadMrz: unknown
): T | undefined {
  const paises: FilaDePais[] = (paisesDeLaCache ?? [])
    .filter((p): p is PaisDeLaCache => !!p)
    .map((p) => ({ id: p.id, displayId: "", code: p.code }));
  if (paises.length === 0) return filaDeNacionalidad(opciones, nacionalidadMrz);
  const pais = filaDeNacionalidad(paises, nacionalidadMrz);
  return pais ? opciones?.find((o) => o.id === pais.id) : undefined;
}
